import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	ICustomSegmentState,
	ISegment,
	ISegmentDetail,
	ISegmentEditLock,
	ISelectedPopulationFilters,
	IVariableDropdownNode,
	IRootNodeNSize,
	IUpdateSegmentEditLockPayload,
} from "../models/segmentInterfaces";

// ─── Helper ───────────────────────────────────────────────────────────────────

const resetViewFlags = (state: ICustomSegmentState) => {
	state.showConfiguration = false;
	state.showSegmentSelectType = false;
	state.showSegmentDetails = false;
};

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: ICustomSegmentState = {
	segments: [],
	segmentsTotalCount: 0,
	hasUnsavedSegment: false,
	showConfiguration: false,
	activeSegmentId: null,
	segmentSearchOpen: false,
	showSegmentSelectType: false,
	isSegmentSelectionBlurred: false,
	isSegmentSelectionProceedEnabled: false,
	showSegmentDetails: false,
	selectedSegmentDetail: null,
	variableDropdownData: [],
	isPreviewPaneExpanded: false,
	isAddingSegment: false,
	isSegmentNameConfirmed: false,
	pendingNewSegmentId: null,
	isNewSegment: false,
	isViewMode: true,
	isValid: false,
	changedPopulationFilters: [],
	rootNodeNSize: null,
	isLockedToCurrentUser: false,
	isSegmentDetailLoading: false,
	isLeftPaneActiveCrud: false,
	pendingSegmentMeta: null,
	isSegmentListLoading: false,
	segmentActiveInsightsId: null,
	showDataUpdateModel: false,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const customSegmentSlice = createSlice({
	name: "customSegment",
	initialState,
	reducers: {
		setSegments(state, action: PayloadAction<ISegment[]>) {
			state.segments = action.payload;
		},
		setSegmentsTotalCount(state, action: PayloadAction<number>) {
			state.segmentsTotalCount = action.payload;
		},
		setHasUnsavedSegment(state, action: PayloadAction<boolean>) {
			state.hasUnsavedSegment = action.payload;
		},
		setActiveSegmentId(state, action: PayloadAction<string | null>) {
			state.activeSegmentId = action.payload;
		},
		setSegmentSearchOpen(state, action: PayloadAction<boolean>) {
			state.segmentSearchOpen = action.payload;
		},
		setIsSegmentSelectionBlurred(state, action: PayloadAction<boolean>) {
			state.isSegmentSelectionBlurred = action.payload;
		},
		setIsSegmentSelectionProceedEnabled(state, action: PayloadAction<boolean>) {
			state.isSegmentSelectionProceedEnabled = action.payload;
		},
		setSelectedSegmentDetail(state, action: PayloadAction<ISegmentDetail | null>) {
			state.selectedSegmentDetail = action.payload;
		},
		setVariableDropdownData(state, action: PayloadAction<IVariableDropdownNode[]>) {
			state.variableDropdownData = action.payload;
		},
		setIsPreviewPaneExpanded(state, action: PayloadAction<boolean>) {
			state.isPreviewPaneExpanded = action.payload;
		},

		// ── Mutually exclusive view setters ──────────────────────────────
		setShowConfiguration(state, action: PayloadAction<boolean>) {
			if (action.payload) resetViewFlags(state);
			state.showConfiguration = action.payload;
		},
		setShowSegmentSelectType(state, action: PayloadAction<boolean>) {
			if (action.payload) resetViewFlags(state);
			state.showSegmentSelectType = action.payload;
		},
		setShowSegmentDetails(state, action: PayloadAction<boolean>) {
			if (action.payload) resetViewFlags(state);
			state.showSegmentDetails = action.payload;
		},

		// ── Add-segment flow actions ──────────────────────────────────────

		startAddSegmentFlow(state) {
			state.isAddingSegment = true;
			state.isSegmentNameConfirmed = false;
			state.hasUnsavedSegment = false;
			state.pendingNewSegmentId = null;
			resetViewFlags(state);
			state.showSegmentSelectType = true;
		},

		confirmSegmentName(state, action: PayloadAction<{ segmentId: string }>) {
			state.isSegmentNameConfirmed = true;
			state.hasUnsavedSegment = false;
			state.isViewMode = false;
			state.pendingNewSegmentId = action.payload.segmentId;
			state.activeSegmentId = action.payload.segmentId;
		},

		cancelAddSegmentFlow(state) {
			state.isNewSegment = false;
			state.isAddingSegment = false;
			state.isSegmentNameConfirmed = false;
			state.hasUnsavedSegment = false;
			state.pendingNewSegmentId = null;
			if (state.segments.length > 0) {
				resetViewFlags(state);
				state.showSegmentDetails = true;
				state.activeSegmentId = state.segments[0].id;
			} else {
				resetViewFlags(state);
				state.showConfiguration = true;
				state.activeSegmentId = null;
			}
		},

		proceedWithSegmentType(state) {
			state.isAddingSegment = false;
			state.isSegmentNameConfirmed = false;
			state.hasUnsavedSegment = true;
			state.pendingNewSegmentId = null;
			resetViewFlags(state);
			state.showSegmentDetails = true;
		},

		setIsNewSegment(state, action: PayloadAction<boolean>) {
			state.isNewSegment = action.payload;
		},
		setIsViewMode(state, action: PayloadAction<boolean>) {
			state.isViewMode = action.payload;
		},
		setIsValid(state, action: PayloadAction<boolean>) {
			state.isValid = action.payload;
		},

		updateSegmentEditLock(state, action: PayloadAction<IUpdateSegmentEditLockPayload>) {
			const { segmentId, edit } = action.payload;
			const seg = state.segments.find((s) => s.id === segmentId);
			if (seg) {
				seg.edit = edit;
				seg.is_locked = edit.edit_lock;
			}
			if (state.selectedSegmentDetail?.id === segmentId) {
				state.selectedSegmentDetail = {
					...state.selectedSegmentDetail,
					edit: {
						edit_lock: edit.edit_lock,
						edit_lock_by: edit.edit_lock_by ? (edit.edit_lock_by as any) : null,
						edit_lock_start_time: edit.edit_lock_start_time ?? null,
						_id: edit._id ?? null,
					},
				};
			}
		},

		setChangedPopulationFilters(state, action: PayloadAction<ISelectedPopulationFilters[]>) {
			state.changedPopulationFilters = action.payload;
		},
		setRootNodeWithNSize(state, action: PayloadAction<IRootNodeNSize | null>) {
			state.rootNodeNSize = action.payload;
		},
		setIsLockedToCurrentUser(state, action: PayloadAction<boolean>) {
			state.isLockedToCurrentUser = action.payload;
		},

		resetSegmentState: (state) => {
			state.segments = [];
			state.segmentsTotalCount = 0;
			state.activeSegmentId = null;
			state.selectedSegmentDetail = null;
			state.hasUnsavedSegment = false;
			state.isAddingSegment = false;
			state.isSegmentNameConfirmed = false;
			state.showSegmentDetails = false;
			state.showSegmentSelectType = false;
			state.showConfiguration = false;
			state.pendingNewSegmentId = null;
			state.isNewSegment = false;
			state.isValid = false;
			state.isPreviewPaneExpanded = false;
			state.variableDropdownData = [];
			state.rootNodeNSize = null;
			state.isSegmentDetailLoading = false;
			state.isLeftPaneActiveCrud = false;
			state.pendingSegmentMeta = null;
			state.isSegmentListLoading = false;
			state.showDataUpdateModel = false;
			state.segmentActiveInsightsId = null;
		},

		setIsSegmentDetailLoading: (state, action: PayloadAction<boolean>) => {
			state.isSegmentDetailLoading = action.payload;
		},
		setIsLeftPaneActiveCrud: (state, action: PayloadAction<boolean>) => {
			state.isLeftPaneActiveCrud = action.payload;
		},
		setPendingSegmentMeta: (state, action: PayloadAction<{ name: string; description: string } | null>) => {
			state.pendingSegmentMeta = action.payload;
		},
		setIsSegmentListLoading: (state, action: PayloadAction<boolean>) => {
			state.isSegmentListLoading = action.payload;
		},
		setSegmentActiveInsightsId: (state, action: PayloadAction<string | null | undefined>) => {
			state.segmentActiveInsightsId = action.payload;
		},
		setShowDataUpdateModel: (state, action: PayloadAction<boolean>) => {
			state.showDataUpdateModel = action.payload;
		},
	},
});

export const {
	setSegments,
	setSegmentsTotalCount,
	setHasUnsavedSegment,
	setActiveSegmentId,
	setSegmentSearchOpen,
	setShowConfiguration,
	setShowSegmentSelectType,
	setIsSegmentSelectionBlurred,
	setIsSegmentSelectionProceedEnabled,
	setShowSegmentDetails,
	setSelectedSegmentDetail,
	setVariableDropdownData,
	setIsPreviewPaneExpanded,
	startAddSegmentFlow,
	confirmSegmentName,
	cancelAddSegmentFlow,
	proceedWithSegmentType,
	setIsNewSegment,
	setIsViewMode,
	setIsValid,
	updateSegmentEditLock,
	setChangedPopulationFilters,
	setRootNodeWithNSize,
	setIsLockedToCurrentUser,
	resetSegmentState,
	setIsSegmentDetailLoading,
	setIsLeftPaneActiveCrud,
	setPendingSegmentMeta,
	setIsSegmentListLoading,
	setSegmentActiveInsightsId,
	setShowDataUpdateModel,
} = customSegmentSlice.actions;

export default customSegmentSlice.reducer;
